import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './RecommendedArticles.css';
import { Article } from '../../types/shared/Article';
import { useWidthContext } from '../../providers/Width';
import { shortenText } from '../../utils/cob-utils';

import { Row, Col, Title, Section, PhotoCard } from '../../importCL';

interface IRecommendedArticlesProps extends MappedComponentProperties {
    version?: 'recommended' | 'related';
    showTitle?: boolean;
    ctaCheck?: boolean;
    articles?: Article[];
    stackVertical?: boolean;
}

export const RecommendedArticlesEditConfig = {
    emptyLabel: 'Recommended Articles',
    isEmpty: (props: PropsWithChildren<IRecommendedArticlesProps>) => {
        return !props || !props.articles || !props.articles.length;
    },
};

const RecommendedArticles = (props: PropsWithChildren<IRecommendedArticlesProps>): JSX.Element => {
    const { version = 'recommended', articles, ctaCheck, showTitle = true, stackVertical = false } = props;
    const isRelated = version === 'related';
    const isThreeArticles = articles && articles.length === 3;
    const isTwoArticles = articles && articles.length === 2;
    const isResourcesStyle = !isRelated && isThreeArticles;
    const useRelatedStyle = isRelated || (!isRelated && isTwoArticles);
    const { width } = useWidthContext();
    
    const generateTitle = () => {
        return (
            <Title 
                className='mb-12 text-center'
                align='center' 
                showHr 
                tag='h4'
            >
                {isRelated ? 'Related Articles' : 'Recommended Articles'}
            </Title>
        );
    };
    
    const generateArticles = () => {
        if (!articles) {
            return null;
        }
        
        return articles.map((article, i) => {
            const articleContent = {
                title: article.title,
                description: useRelatedStyle || width <= 768 ? '': shortenText(article.previewDescription),
                image: `${article.imagePath}?imwidth=360`,
                imageAltText: article.imageAlt,
                link: article.pagePath,
                target: '_self' as TAnchorTarget,
            };
            
            if (useRelatedStyle) {
                return (
                    <Col key={i} className='[@media(max-width:375px)]:w-full md:w-1/2 lg:w-1/3'>
                        <PhotoCard 
                            {...(ctaCheck ? { anchorText: 'Read more' } : {})}
                            tag='article'
                            cardContent={articleContent} 
                            horizontal={true} 
                            untruncate
                        />
                    </Col>
                );
            }
            
            if (isResourcesStyle) {
                return (
                    <Col
                        xs={6}
                        md={4}
                        key={i}
                    >
                        {width <= 768 &&
                            <PhotoCard 
                                tag='article'
                                untruncate
                                horizontal={true} 
                                aria-label={article.title}
                                cardContent={articleContent}
                            />}
                        {width > 768 &&
                            <PhotoCard 
                                tag='article'
                                untruncate
                                anchorText='Read more'
                                chevron={true}
                                aria-label={article.title}
                                cardContent={articleContent}
                            />}
                    </Col>
                );
            }
            
            return (
                <Col
                    xs={12} md={6} lg={4}
                    key={i}
                >
                    <PhotoCard 
                        shadowless 
                        tag='article'
                        cardContent={articleContent} 
                        untruncate 
                        titleTag='h5'
                    />
                </Col>
            );
        });
    };
    
    return (
        <Section 
            id='recommended-articles' 
            className={`${
                isResourcesStyle ? '' : 'RecommendedArticles--related'
            } px-0 py-6`}
        >
            {showTitle && generateTitle()}
            <Row className={`flex ${stackVertical ? 'flex-col gap-[1rem] m-0 p-0': 'mb-12 flex-col md:gap-[1rem] md:p-4'}`}>
                {generateArticles()}
            </Row>
        </Section>
    );
};

export default RecommendedArticles;
